import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useProject } from ':cloud/contexts/ProjectContext';
import { useGetProject } from ':cloud/hooks/useGetProject';
import { useGetProjects } from ':cloud/queries/Projects/useGetProjects';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { AppRoute } from ':cloud/utils/routes';

/**
 * This hook is responsible for navigating to the correct project based on the
 * projectId param in the URL. It also updates the selected project in the app
 * state, ensuring that the project properties are always up to date.
 * e.g. Project name, users, roles/permissions, etc.
 *
 * DO NOT manually set `selectedProject` anywhere else in the app. If you want
 * to change the selected project, update the `projectId` param in the URL and
 * let this hook handle the rest.
 */
function useProjectNavigation() {
  const history = useHistory();
  const location = useLocation();
  const { activeOrg } = useGetUser();
  const { selectedProjectId, setSelectedProjectId, setSelectedProject } = useProject();
  const { projects, isLoading: isProjectsLoading } = useGetProjects(activeOrg?.organizationId);

  const projectIdParam = new URLSearchParams(location.search).get('projectId');
  const previousProjectIdRef = useRef(projectIdParam);
  const foundProject = useGetProject(projectIdParam || '');

  // Make sure the projectId param in the URL exists and is valid
  useEffect(
    () => {
      if (!projects?.length || isProjectsLoading) return;

      // We can assume that this will always exist
      const fallbackProject = projects[0];

      // If there's no projectId param, set it to the selected project id or the fallback project id.
      // In both cases, the effect will run again.
      if (!projectIdParam) {
        const newProjectId = selectedProjectId || fallbackProject.id;
        history.replace(`${location.pathname}?projectId=${newProjectId}`);
        return;
      }

      // If the projectId param exists but is invalid (suggesting the user does not have access to the project),
      // we reset it to the fallback project id & the effect will run again
      if (!foundProject) {
        history.replace(`${AppRoute.Projects.Overview}?projectId=${fallbackProject.id}`);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- including selectedProjectId causes infinite re-renders
    [isProjectsLoading, projects, foundProject, history, location.pathname, projectIdParam],
  );

  // Update the selected project id in localStorage so the user's current working project is retained
  // even if they navigate away from the Portal and come back
  useEffect(
    () => {
      if (!foundProject || previousProjectIdRef.current === projectIdParam) return;

      previousProjectIdRef.current = projectIdParam;
      if (foundProject && selectedProjectId !== projectIdParam) {
        setSelectedProjectId(foundProject.id);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedProjectId lives in localStorage; including it in the dependency array causes infinite re-renders
    [foundProject, setSelectedProjectId, projectIdParam],
  );

  // If everything looks good w/ the project id, update the selected project in the app state
  useEffect(() => {
    if (!foundProject) return;
    setSelectedProjectId(foundProject.id);
    setSelectedProject(foundProject);
  }, [foundProject, setSelectedProject, setSelectedProjectId]);

  return {
    foundProject,
    projectIdParam,
  };
}

export default useProjectNavigation;
